import React, { useState, useEffect } from 'react';
import { Settings, Signal, Truck, Check, MapPin, Loader2, AlertCircle, Info, Home, Percent } from 'lucide-react';
import { Button } from './ui/button';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const ServiceOptions = ({ onServicesChange, showTitle = true }) => {
  const [selectedServices, setSelectedServices] = useState({
    programming: false,
    subscription_4g: null, // null, 'monthly', 'annual'
    installation: false,
  });
  
  const [city, setCity] = useState('');
  const [distanceResult, setDistanceResult] = useState(null);
  const [loadingDistance, setLoadingDistance] = useState(false);
  const [showInstallationForm, setShowInstallationForm] = useState(false);
  const [confirmed2Years, setConfirmed2Years] = useState(false);

  // Calculate total and notify parent
  useEffect(() => {
    const services = [];
    let total = 0;

    // Programming (only if installation not selected)
    if (selectedServices.programming && !selectedServices.installation) {
      services.push({
        service_id: 'srv_programming',
        name: 'Programmation seule',
        price: 150,
        price_type: 'one_time'
      });
      total += 150;
    }

    // 4G Subscription
    if (selectedServices.subscription_4g === 'monthly') {
      services.push({
        service_id: 'srv_4g_monthly',
        name: 'Forfait 4G Ajax (Mensuel)',
        price: 8.90,
        price_type: 'monthly'
      });
      total += 8.90;
    } else if (selectedServices.subscription_4g === 'annual') {
      services.push({
        service_id: 'srv_4g_annual',
        name: 'Forfait 4G Ajax (Annuel)',
        price: 99,
        price_type: 'annual'
      });
      total += 99;
    }

    // Installation (includes programming)
    if (selectedServices.installation && distanceResult?.installation_available) {
      services.push({
        service_id: 'srv_installation',
        name: `Installation à ${distanceResult.city}`,
        price: distanceResult.installation_price,
        price_type: 'one_time',
        distance_km: distanceResult.distance_km,
        confirmed_2_years: confirmed2Years
      });
      total += distanceResult.installation_price;
    }

    if (onServicesChange) {
      onServicesChange(services, total, selectedServices.installation && confirmed2Years);
    }
  }, [selectedServices, distanceResult, confirmed2Years, onServicesChange]);

  const handleCitySearch = async () => {
    if (!city.trim()) return;
    
    setLoadingDistance(true);
    try {
      const response = await fetch(`${API}/services/calculate-distance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ city: city.trim() })
      });
      
      if (response.ok) {
        const result = await response.json();
        setDistanceResult(result);
        if (result.installation_available) {
          setSelectedServices(prev => ({ ...prev, installation: true }));
        }
      }
    } catch (error) {
      console.error('Error calculating distance:', error);
    } finally {
      setLoadingDistance(false);
    }
  };

  const handleProgrammingToggle = () => {
    // Can't select programming if installation is selected (programming is included)
    if (selectedServices.installation) return;
    setSelectedServices(prev => ({
      ...prev,
      programming: !prev.programming
    }));
  };

  const handleInstallationToggle = () => {
    if (!distanceResult?.installation_available) {
      setShowInstallationForm(true);
      return;
    }
    setSelectedServices(prev => ({
      ...prev,
      installation: !prev.installation,
      // Deselect programming when installation is selected (it's included)
      programming: !prev.installation ? false : prev.programming
    }));
  };

  const handle4GToggle = (type) => {
    setSelectedServices(prev => ({
      ...prev,
      subscription_4g: prev.subscription_4g === type ? null : type
    }));
  };

  return (
    <div className="space-y-4" data-testid="service-options">
      {showTitle && (
        <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
          <Settings className="w-5 h-5 text-emerald-400" />
          Services optionnels
        </h3>
      )}

      {/* Programming Option */}
      <div 
        className={`p-4 rounded-xl border transition-all cursor-pointer ${
          selectedServices.programming && !selectedServices.installation
            ? 'bg-emerald-500/10 border-fgs-primary'
            : selectedServices.installation
            ? 'bg-white/[0.02]/50 border-white/5/50 opacity-60'
            : 'bg-white/[0.02] border-white/5 hover:border-fgs-primary/50'
        }`}
        onClick={handleProgrammingToggle}
        data-testid="service-programming"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              selectedServices.programming && !selectedServices.installation ? 'bg-emerald-500' : 'bg-white/[0.02]-highlight'
            }`}>
              <Settings className={`w-5 h-5 ${selectedServices.programming && !selectedServices.installation ? 'text-white' : 'text-white/50'}`} />
            </div>
            <div>
              <h4 className="font-semibold text-white">Programmation seule</h4>
              <p className="text-sm text-white/50">Configuration de votre centrale Ajax par nos techniciens</p>
              {selectedServices.installation && (
                <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Inclus dans l'installation
                </p>
              )}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <span className="font-mono text-lg font-bold text-emerald-400">150 €</span>
            <p className="text-xs text-white/50">unique</p>
          </div>
        </div>
      </div>

      {/* 4G Subscription Option */}
      <div className="p-4 rounded-xl border bg-white/[0.02] border-white/5" data-testid="service-4g">
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            selectedServices.subscription_4g ? 'bg-emerald-500' : 'bg-white/[0.02]-highlight'
          }`}>
            <Signal className={`w-5 h-5 ${selectedServices.subscription_4g ? 'text-white' : 'text-white/50'}`} />
          </div>
          <div>
            <h4 className="font-semibold text-white">Forfait 4G Ajax Systems</h4>
            <p className="text-sm text-white/50">Connexion 4G sans engagement pour votre centrale</p>
            <p className="text-xs text-white/50 mt-1 italic">TVA 20% applicable (service télécom)</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handle4GToggle('monthly')}
            className={`p-3 rounded-lg border text-left transition-all ${
              selectedServices.subscription_4g === 'monthly'
                ? 'bg-emerald-500/10 border-fgs-primary'
                : 'bg-black/40 border-white/5 hover:border-fgs-primary/50'
            }`}
            data-testid="service-4g-monthly"
          >
            <div className="flex items-center justify-between">
              <span className="text-white text-sm font-medium">Mensuel</span>
              {selectedServices.subscription_4g === 'monthly' && (
                <Check className="w-4 h-4 text-emerald-400" />
              )}
            </div>
            <div className="mt-1">
              <span className="font-mono text-lg font-bold text-emerald-400">8,90 €</span>
              <span className="text-xs text-white/50">/mois</span>
            </div>
          </button>
          
          <button
            onClick={() => handle4GToggle('annual')}
            className={`p-3 rounded-lg border text-left transition-all ${
              selectedServices.subscription_4g === 'annual'
                ? 'bg-emerald-500/10 border-fgs-primary'
                : 'bg-black/40 border-white/5 hover:border-fgs-primary/50'
            }`}
            data-testid="service-4g-annual"
          >
            <div className="flex items-center justify-between">
              <span className="text-white text-sm font-medium">Annuel</span>
              {selectedServices.subscription_4g === 'annual' && (
                <Check className="w-4 h-4 text-emerald-400" />
              )}
            </div>
            <div className="mt-1">
              <span className="font-mono text-lg font-bold text-emerald-400">99 €</span>
              <span className="text-xs text-white/50">/an</span>
            </div>
            <p className="text-xs text-fgs-success mt-1">Économisez 7,80 €</p>
          </button>
        </div>
      </div>

      {/* Installation Option */}
      <div className="p-4 rounded-xl border bg-white/[0.02] border-white/5" data-testid="service-installation">
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            selectedServices.installation ? 'bg-emerald-500' : 'bg-white/[0.02]-highlight'
          }`}>
            <Truck className={`w-5 h-5 ${selectedServices.installation ? 'text-white' : 'text-white/50'}`} />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-white">Installation à domicile</h4>
            <p className="text-sm text-white/50">Installation + programmation par nos techniciens certifiés</p>
            <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
              <Info className="w-3 h-3" />
              Programmation incluse
            </p>
          </div>
        </div>

        {/* City Search */}
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCitySearch()}
                placeholder="Entrez votre ville..."
                className="w-full pl-10 pr-4 py-2 bg-black/40 border border-white/5 rounded-lg text-white placeholder:text-white/50 focus:border-fgs-primary focus:outline-none"
                data-testid="installation-city-input"
              />
            </div>
            <Button
              onClick={handleCitySearch}
              disabled={loadingDistance || !city.trim()}
              className="bg-emerald-500 hover:bg-emerald-500-hover px-4"
              data-testid="installation-search-btn"
            >
              {loadingDistance ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Vérifier'
              )}
            </Button>
          </div>

          {/* Distance Result */}
          {distanceResult && (
            <div className={`p-3 rounded-lg ${
              distanceResult.installation_available 
                ? 'bg-fgs-success/10 border border-fgs-success/30' 
                : 'bg-fgs-accent/10 border border-fgs-accent/30'
            }`}>
              {distanceResult.installation_available ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium flex items-center gap-2">
                      <Check className="w-4 h-4 text-fgs-success" />
                      {distanceResult.city} ({distanceResult.distance_km} km)
                    </p>
                    <p className="text-sm text-white/50">Installation disponible</p>
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-xl font-bold text-emerald-400">
                      {distanceResult.installation_price} €
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-fgs-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">{distanceResult.city} ({distanceResult.distance_km} km)</p>
                    <p className="text-sm text-white/50">{distanceResult.message}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Select Installation */}
          {distanceResult?.installation_available && (
            <div className="space-y-3">
              <button
                onClick={handleInstallationToggle}
                className={`w-full p-3 rounded-lg border transition-all ${
                  selectedServices.installation
                    ? 'bg-emerald-500/10 border-fgs-primary'
                    : 'bg-black/40 border-white/5 hover:border-fgs-primary/50'
                }`}
                data-testid="installation-select-btn"
              >
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">
                    {selectedServices.installation ? 'Installation sélectionnée' : 'Ajouter l\'installation'}
                  </span>
                  {selectedServices.installation && <Check className="w-5 h-5 text-emerald-400" />}
                </div>
              </button>

              {/* TVA 10% - Confirmation 2 ans */}
              {selectedServices.installation && (
                <div className="bg-gradient-to-r from-green-500/10 to-fgs-primary/10 border border-green-500/40 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Percent className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-white font-medium text-sm">
                        TVA réduite à 10%
                      </p>
                      <p className="text-xs text-white/50 mt-1">
                        Bénéficiez de la TVA à 10% au lieu de 20% sur toute votre commande
                      </p>
                      
                      <label className="flex items-start gap-3 cursor-pointer mt-3 p-3 bg-black/40/50 rounded-lg border border-white/5 hover:border-green-500/50 transition-colors">
                        <input
                          type="checkbox"
                          checked={confirmed2Years}
                          onChange={(e) => setConfirmed2Years(e.target.checked)}
                          className="w-5 h-5 rounded border-white/5 bg-white/[0.02] text-green-500 focus:ring-green-500 mt-0.5"
                          data-testid="confirm-2-years-checkbox"
                        />
                        <div>
                          <span className="text-white text-sm">
                            Je certifie que mon logement est achevé depuis plus de 2 ans
                          </span>
                          <p className="text-xs text-white/50 mt-1">
                            (Article 279-0 bis du CGI)
                          </p>
                        </div>
                      </label>

                      {confirmed2Years && (
                        <div className="mt-3 flex items-center gap-2 text-green-500 text-sm bg-green-500/10 p-2 rounded">
                          <Check className="w-4 h-4" />
                          <span className="font-medium">TVA 10% appliquée sur votre commande</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pricing Info */}
          {!distanceResult && (
            <div className="bg-black/40/50 rounded-lg p-3">
              <p className="text-xs text-white/50 mb-2">Tarifs installation (autour de Montpellier) :</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-white/[0.02] rounded p-2">
                  <p className="text-emerald-400 font-mono font-bold">350 €</p>
                  <p className="text-xs text-white/50">≤ 100 km</p>
                </div>
                <div className="bg-white/[0.02] rounded p-2">
                  <p className="text-emerald-400 font-mono font-bold">450 €</p>
                  <p className="text-xs text-white/50">≤ 150 km</p>
                </div>
                <div className="bg-white/[0.02] rounded p-2">
                  <p className="text-emerald-400 font-mono font-bold">490 €</p>
                  <p className="text-xs text-white/50">≤ 200 km</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceOptions;
